import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Grid,
  Alert,
  Switch,
  FormControlLabel,
  Table, TableHead, TableRow, TableCell, TableBody,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { useAuthContext } from "../context/AuthContext";

const n = (v) => (v === "" || v == null ? null : Number(v));
const digitsOnly = (s) => String(s ?? "").replace(/[^\d]/g, "");

// >>> Sempre prefixa com tmp_ (independente de crypto.randomUUID)
const genId = () => {
  const rnd = (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now()}_${Math.random().toString(16).slice(2)}`;
  return `tmp_${rnd}`;
};

/* Dialog de Subcategoria */
const SubDialog = ({ open, initial, onClose, onSave }) => {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({
    id: initial?.id ?? undefined,
    nome: initial?.nome ?? "",
    ordem: initial?.ordem ?? "",
    status: initial?.status == null ? 1 : Number(initial.status),
  });

  useEffect(() => {
    setForm({
      id: initial?.id ?? undefined,
      nome: initial?.nome ?? "",
      ordem: initial?.ordem ?? "",
      status: initial?.status == null ? 1 : Number(initial.status),
    });
  }, [initial?.id]);

  const canSave = form.nome.trim().length > 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? "Editar Subcategoria" : "Nova Subcategoria"}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField
              label="Nome *"
              value={form.nome}
              onChange={(e) => setForm((p) => ({ ...p, nome: e.target.value }))}
              fullWidth
              autoFocus
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Ordem"
              value={form.ordem ?? ""}
              onChange={(e) => setForm((p) => ({ ...p, ordem: digitsOnly(e.target.value) }))}
              inputMode="numeric"
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={Number(form.status) === 1}
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.checked ? 1 : 0 }))}
                />
              }
              label={Number(form.status) === 1 ? "Ativa" : "Inativa"}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          disabled={!canSave}
          onClick={() =>
            onSave({
              id: form.id,
              nome: form.nome.trim(),
              ordem: n(form.ordem),
              status: Number(form.status) === 1 ? 1 : 0,
            })
          }
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const AddCategoryForm = ({ categoryData, onClose, onSubmit, isLoading }) => {
  const { userData } = useAuthContext();
  const [errorMessage, setErrorMessage] = useState(null);

  const [formData, setFormData] = useState({
    ...categoryData,
    status: categoryData?.status == null ? 1 : Number(categoryData.status),
    ordem: categoryData?.ordem ?? "",
    children: Array.isArray(categoryData?.children) ? [...categoryData.children] : [],
    id_usuario: categoryData?.id_usuario || userData?.id,
    id_usuario_ultima_alteracao: userData?.id,
    id_pai: categoryData?.id_pai ?? undefined,
  });

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      ...categoryData,
      status: categoryData?.status == null ? 1 : Number(categoryData.status),
      ordem: categoryData?.ordem ?? "",
      children: Array.isArray(categoryData?.children) ? [...categoryData.children] : [],
      id_usuario: categoryData?.id_usuario || userData?.id,
      id_usuario_ultima_alteracao: userData?.id,
      id_pai: categoryData?.id_pai ?? undefined,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryData?.id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Subcategorias no formulário (persistem no submit do pai)
  const [subDlg, setSubDlg] = useState({ open: false, initial: null });

  const addSub = () => setSubDlg({ open: true, initial: null });
  const editSub = (sub) => setSubDlg({ open: true, initial: sub });
  const removeSub = (id) =>
    setFormData((p) => ({ ...p, children: (p.children || []).filter((s) => String(s.id) !== String(id)) }));

  const saveSub = (sub) => {
    setFormData((p) => {
      const list = Array.isArray(p.children) ? [...p.children] : [];
      if (sub.id) {
        const idx = list.findIndex((s) => String(s.id) === String(sub.id));
        if (idx >= 0) list[idx] = { ...list[idx], ...sub };
        else list.push(sub);
      } else {
        list.push({ ...sub, id: genId() }); // <<< sempre tmp_
      }
      return { ...p, children: list };
    });
    setSubDlg({ open: false, initial: null });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(null);

    const payload = {
      id: formData.id,
      nome: (formData.nome || "").trim(),
      status: Number(formData.status) === 1 ? 1 : 0,
      ...(formData.id_pai != null ? { id_pai: Number(formData.id_pai) } : {}),
      ...(formData.ordem !== "" ? { ordem: Number(digitsOnly(formData.ordem)) } : {}),
      children: (formData.children || [])
        .filter((s) => (s?.nome || "").trim() !== "")
        .map((s) => ({
          id: s.id && !String(s.id).startsWith("tmp_") ? s.id : undefined, // novos sem id
          nome: s.nome.trim(),
          ordem: n(digitsOnly(s.ordem)),
          status: Number(s.status) === 1 ? 1 : 0,
        })),
    };

    // console.log("[AddCategoryForm] submitting payload:", payload);
    try {
      await onSubmit(payload);
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || "Erro ao salvar a categoria.");
    }
  };

  const subs = (formData.children || []).slice().sort((a, b) => (a.ordem || 0) - (b.ordem || 0));

  return (
    <Paper elevation={3} sx={{ padding: 3, width: "95%", margin: "0 auto" }}>
      <Typography variant="h6" gutterBottom>
        {categoryData?.id ? "Editar Categoria" : "Adicionar Categoria"}
      </Typography>

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField
              label="Nome"
              name="nome"
              value={formData.nome || ""}
              onChange={handleChange}
              fullWidth
              required
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Ordem"
              name="ordem"
              value={formData.ordem ?? ""}
              onChange={(e) =>
                setFormData((p) => ({ ...p, ordem: digitsOnly(e.target.value) }))
              }
              inputMode="numeric"
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="ID do Pai (opcional)"
              name="id_pai"
              value={formData.id_pai ?? ""}
              onChange={(e) => setFormData((p) => ({ ...p, id_pai: digitsOnly(e.target.value) || undefined }))}
              inputMode="numeric"
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={Number(formData.status) === 1}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, status: e.target.checked ? 1 : 0 }))
                  }
                />
              }
              label={Number(formData.status) === 1 ? "Ativa" : "Inativa"}
            />
          </Grid>
        </Grid>

        {/* Subcategorias do formulário */}
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="subtitle1">Subcategorias</Typography>
            <Button size="small" variant="outlined" startIcon={<AddRoundedIcon />} onClick={addSub}>
              Adicionar subcategoria
            </Button>
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={120}>Ordem</TableCell>
                <TableCell>Nome</TableCell>
                <TableCell width={140}>Status</TableCell>
                <TableCell align="right" width={120}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary">
                      Nenhuma subcategoria adicionada.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                subs.map((s) => (
                  <TableRow key={s.id || s.nome}>
                    <TableCell>{s.ordem ?? ""}</TableCell>
                    <TableCell>{s.nome}</TableCell>
                    <TableCell>{Number(s.status) === 1 ? "Ativa" : "Inativa"}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => editSub(s)}>
                          <EditRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton size="small" onClick={() => removeSub(s.id)}>
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
          <Button variant="outlined" onClick={onClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {isLoading ? <CircularProgress size={20} color="inherit" /> : "Salvar"}
          </Button>
        </Box>
      </form>

      <SubDialog
        open={subDlg.open}
        initial={subDlg.initial}
        onClose={() => setSubDlg({ open: false, initial: null })}
        onSave={saveSub}
      />
    </Paper>
  );
};

export default AddCategoryForm;
